import React, { useState, useEffect } from "react";
import { ApprovalRequest, TraceabilityData } from "../../types";
import PRDiffViewer from "./PRDiffViewer";
import ContextPanel from "./ContextPanel";
import TraceabilityFlow from "./TraceabilityFlow";
import ApprovalControls from "./ApprovalControls";
import { useApprovalActions } from "../../hooks/useApprovalActions";
import { Tabs, TabsList, TabsTrigger } from "../Common/Tabs";

interface ApprovalDetailProps {
  approval: ApprovalRequest;
  onApprovalAction: (
    approvalId: string,
    action: "approve" | "reject",
    comment?: string
  ) => Promise<void>;
  className?: string;
}

interface ApprovalDetails {
  id: string;
  prUrl: string;
  diff: string;
  jiraTicket?: any;
  pseudocodeRef?: string;
  testResults?: any;
  comments: Array<{
    id: string;
    author: string;
    content: string;
    createdAt: string;
  }>;
}

const ApprovalDetail: React.FC<ApprovalDetailProps> = ({
  approval,
  onApprovalAction,
  className = "",
}) => {
  const [activeTab, setActiveTab] = useState<
    "diff" | "context" | "traceability"
  >("diff");
  const [details, setDetails] = useState<ApprovalDetails | null>(null);
  const [traceability, setTraceability] = useState<TraceabilityData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { fetchApprovalDetails, fetchTraceability } = useApprovalActions();

  useEffect(() => {
    const loadApprovalData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load approval details and traceability in parallel
        const [detailsData, traceabilityData] = await Promise.all([
          fetchApprovalDetails(approval.id),
          fetchTraceability(approval.id),
        ]);

        setDetails(detailsData);
        setTraceability(traceabilityData);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load approval details"
        );
      } finally {
        setLoading(false);
      }
    };

    loadApprovalData();
  }, [approval.id, fetchApprovalDetails, fetchTraceability]);

  const handleApprovalAction = async (
    action: "approve" | "reject",
    comment?: string
  ) => {
    try {
      await onApprovalAction(approval.id, action, comment);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to process approval"
      );
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-64 ${className}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading approval details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`bg-red-50 border border-red-200 rounded-lg p-4 m-4 ${className}`}
      >
        <div className="text-red-800">
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  if (!details) {
    return (
      <div
        className={`bg-yellow-50 border border-yellow-200 rounded-lg p-4 m-4 ${className}`}
      >
        <div className="text-yellow-800">
          No details available for this approval.
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Header */}
      <div className="p-4 border-b bg-white">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              {approval.title}
            </h2>
            <p className="text-sm text-gray-600 mb-2">{approval.description}</p>
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              <span>Workflow: {approval.workflowId}</span>
              {approval.context.jiraTicket && (
                <span>Ticket: {approval.context.jiraTicket}</span>
              )}
              {approval.context.prNumber && (
                <span>PR: #{approval.context.prNumber}</span>
              )}
              <span>
                Waiting: {Math.floor(approval.waitingTime / 60)}h{" "}
                {approval.waitingTime % 60}m
              </span>
            </div>
          </div>

          <div className="ml-4">
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                approval.priority === "high"
                  ? "bg-red-100 text-red-800"
                  : approval.priority === "medium"
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-green-100 text-green-800"
              }`}
            >
              {approval.priority} priority
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b bg-white">
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as any)}
        >
          <TabsList className="w-full justify-start px-4">
            <TabsTrigger value="diff">Code Changes</TabsTrigger>
            <TabsTrigger value="context">Context</TabsTrigger>
            <TabsTrigger value="traceability">Traceability</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Content Area */}
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 overflow-hidden">
          {activeTab === "diff" && (
            <PRDiffViewer
              diff={details.diff}
              prUrl={details.prUrl}
              className="h-full"
            />
          )}

          {activeTab === "context" && (
            <ContextPanel
              jiraTicket={details.jiraTicket}
              pseudocodeRef={details.pseudocodeRef}
              testResults={details.testResults}
              comments={details.comments}
              className="h-full"
            />
          )}

          {activeTab === "traceability" && traceability && (
            <TraceabilityFlow traceability={traceability} className="h-full" />
          )}
        </div>

        {/* Right Panel - Approval Controls */}
        <div className="w-80 border-l bg-gray-50">
          <ApprovalControls
            approval={approval}
            onApprove={(comment) => handleApprovalAction("approve", comment)}
            onReject={(comment) => handleApprovalAction("reject", comment)}
            className="h-full"
          />
        </div>
      </div>
    </div>
  );
};

export default ApprovalDetail;
